"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { setPublicCompositionLocation, scrollPublicCompositionSection } from "./public-composition-context";

import { cn } from "@/lib/utils";
import type { PublicVirtualSpaceRailSection } from "@/lib/virtualspace/public-virtual-space-rail";
import { VIRTUALSPACE_PUBLIC_SCROLL_CLASS } from "@/lib/virtualspace/virtualspace-public-viewport";
import { VirtualSpaceTheaterProvider } from "@/components/virtualspace/virtual-space-theater-controller";

type Props = {
  sections: PublicVirtualSpaceRailSection[];
  children: ReactNode;
  standaloneEnhancements?: boolean;
};

/**
 * Public VirtualSpace scroll rail.
 *
 * Visual model:
 *   - **Wide viewports (sm+)** — a thin vertical pill of dots, fixed to the
 *     right edge inside the scroll-column's reserved right gutter. Labels
 *     show via the browser's native tooltip on dot hover; they're never
 *     painted onto the page (so labels can never visually clip into media
 *     or off-screen). The rail fades out under `data-vs-theater-root="1"`
 *     on `<html>` (set by the theater controller) so an active media
 *     block can read as the dominant layer; pointer-enter or focus brings
 *     it back to full opacity.
 *   - **Narrow viewports (<sm)** — a sticky compact dot-strip docked at
 *     the top of the scroll band, same labels-via-title behavior.
 *
 * Targeting:
 *   - Every entry scrolls to `[data-vs-rail-block-id="<id>"]` so filtering
 *     Spacer blocks out of the rail keeps targets correct even though page
 *     indexes diverge from rail indexes.
 *   - Pre-2026-05-06 the rail also carried a synthetic "Intro" entry with
 *     no `blockIndex` that targeted `[data-vs-rail-index="0"]` (a
 *     Copy-link / Share strip rendered above the renderer). Those controls
 *     now live in the page header, the body intro is gone, and the rail
 *     starts at the first content block.
 *
 * Theater integration:
 *   - The provider mounts the IntersectionObserver against this scroll
 *     ref, mirrors active state to `<html data-vs-theater-root>` and to
 *     the active block via `data-vs-theater="active"`. Global CSS in
 *     `globals.css` does the rest (width grow, surrounding dim, rail
 *     fade).
 */
export function VirtualSpacePublicScrollRail({ sections, children, standaloneEnhancements = false }: Props) {
  /*
    Post-2026-05-07: the page uses BODY scroll (not an inner
    overflow-y-auto band) so iOS Safari's tap-status-bar-to-top
    gesture works. The "scroll root" everywhere below is the
    document viewport — `scrollRef` is null and the
    IntersectionObserver / theater controller observe with
    `root: null`. We still keep `scrollRef` exposed so future
    refactors can swap back to an inner scroller without
    rewiring every consumer.
  */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const [contentsOpen, setContentsOpen] = useState(false);
  const selectedSection = useRef<HTMLElement | null>(null);
  const closeScrollFrame = useRef<number | null>(null);
  useEffect(() => () => {
    if (closeScrollFrame.current !== null) cancelAnimationFrame(closeScrollFrame.current);
  }, []);

  useEffect(() => {
    if (sections.length < 2) return;
    if (typeof window === "undefined") return;

    let frame = 0;
    const compute = () => {
      const viewTop = window.innerHeight * 0.18;
      const viewBottom = window.innerHeight * 0.6;

      const sectionEls: { idx: number; el: HTMLElement }[] = [];
      sections.forEach((s, i) => {
        const el = document.querySelector<HTMLElement>(
          `[data-vs-rail-block-id="${s.id}"]`,
        );
        if (el) sectionEls.push({ idx: i, el });
      });
      if (sectionEls.length === 0) return;

      let bestIdx = 0;
      let bestOverlap = -1;
      for (const { idx, el } of sectionEls) {
        const r = el.getBoundingClientRect();
        const overlap =
          Math.max(0, Math.min(r.bottom, viewBottom) - Math.max(r.top, viewTop)) /
          Math.max(1, r.height * 0.4);
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestIdx = idx;
        }
      }
      setActive((a) => (a === bestIdx ? a : bestIdx));
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(compute);
    };

    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [sections]);

  const scrollTo = useCallback(
    (i: number) => {
      const target = sections[i];
      if (!target) return;
      const el = document.querySelector<HTMLElement>(
        `[data-vs-rail-block-id="${target.id}"]`,
      );
      const behavior = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
      if (standaloneEnhancements) {
        setPublicCompositionLocation({ section: target.id });
        if (el && contentsOpen) selectedSection.current = el;
        else if (el) scrollPublicCompositionSection(el, behavior);
      } else {
        el?.scrollIntoView({ behavior, block: "start" });
      }
      setContentsOpen(false);
    },
    [sections, standaloneEnhancements, contentsOpen],
  );

  if (sections.length < 2) {
    return (
      <div ref={scrollRef} className={VIRTUALSPACE_PUBLIC_SCROLL_CLASS}>
        <VirtualSpaceTheaterProvider scrollRootRef={scrollRef} disabled>
          {children}
        </VirtualSpaceTheaterProvider>
      </div>
    );
  }

  return (
    <div className="relative">
      {standaloneEnhancements ? <div className="px-4 pt-3 sm:hidden">
        <Dialog open={contentsOpen} onOpenChange={setContentsOpen}>
          <DialogTrigger asChild><button type="button" className="vs-btn-outline flex min-h-11 w-full items-center justify-between px-4"><span>Sections</span><span className="truncate pl-3 text-xs text-muted-foreground">{sections[active]?.label}</span></button></DialogTrigger>
          <DialogContent className="max-h-[80dvh] overflow-y-auto" onCloseAutoFocus={event => {
            const target = selectedSection.current;
            if (!target) return;
            event.preventDefault();
            selectedSection.current = null;
            // Radix normally focuses the trigger, which scrolls the page back
            // to the contents button. A section choice instead transfers focus
            // and scrolls after the modal's scroll lock has been removed.
            closeScrollFrame.current = requestAnimationFrame(() => {
              closeScrollFrame.current = null;
              target.focus({ preventScroll: true });
              const behavior = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
              scrollPublicCompositionSection(target, behavior);
            });
          }}>
            <DialogTitle>Sections</DialogTitle>
            <DialogDescription>Jump to a section in this project.</DialogDescription>
            <nav aria-label="Project sections" className="space-y-1">
              {sections.map((section, index) => <button key={section.id} type="button" onClick={() => scrollTo(index)} aria-current={index === active ? "location" : undefined} className="flex min-h-11 w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-muted">{section.label}</button>)}
            </nav>
          </DialogContent>
        </Dialog>
      </div> : null}
      <div
        ref={scrollRef}
        className={cn(VIRTUALSPACE_PUBLIC_SCROLL_CLASS, "relative")}
      >
        {/* Mobile: NO rail. The pre-2026-05-07 sticky dot-strip
            doubled as a chapter indicator on narrow screens but
            (a) overlapped the BIM viewer + other media as the
            user scrolled, (b) had unreadable dot tap targets at
            phone widths, and (c) added net zero value because
            phone users navigate by natural scroll, not by 13
            tiny dots. Mobile customers get a clean, scrollable
            page with no sticky overlay. The desktop rail
            (below) is the canonical Space-navigation surface
            for pointer / keyboard users.

            Note: the wrapper carries `scrollRef` purely as a
            marker for the theater controller — it's NOT the
            scroll container anymore (body scroll governs that
            post-2026-05-07). The controller listens on
            `window` for scroll events and observes against
            `root: null`; the wrapper just gives the controller
            a stable element to query for theater-eligible
            descendants and project state attributes onto. */}
        <VirtualSpaceTheaterProvider scrollRootRef={scrollRef}>
          {children}
        </VirtualSpaceTheaterProvider>
      </div>

      {/* Wide: hover-expanding pill. Default state shows a column of
          dots (28 px wide); on hover or focus it expands to ~14 rem
          and reveals the operator-supplied label next to each dot.
          Click anywhere in the row to jump to that Space. The
          `vs-public-rail` class lets `globals.css` fade the rail
          under theater state.

          Pre-2026-05-06 the rail was dots-only with native tooltips
          ("title=…"). Founder feedback: "I don't know how useful
          this is if we cant name each section" — the per-block
          nav_label feature shipped first, then this label-on-hover
          treatment so operator-set names actually surface in the
          rail without taking permanent horizontal real estate. */}
      <aside
        aria-label="Spaces in this VirtualSpace"
        className={cn(
          "vs-public-rail pointer-events-auto fixed z-20 hidden flex-col items-stretch sm:flex",
          "right-[max(0.75rem,env(safe-area-inset-right,0px))] top-24 bottom-28 lg:right-[max(1rem,env(safe-area-inset-right,0px))]",
          "transition-opacity duration-200",
        )}
      >
        <ol
          className={cn(
            "vs-glass-floating group/rail flex max-h-[min(70vh,30rem)] flex-col items-stretch gap-0.5 overflow-y-auto overscroll-y-contain",
            "rounded-2xl px-1 py-2 transition-[width] duration-200 ease-out",
            standaloneEnhancements ? "w-[52px] hover:w-56 focus-within:w-56" : "w-9 hover:w-56 focus-within:w-56",
          )}
        >
          {sections.map((s, i) => (
            <li key={s.id} className="w-full">
              <button
                type="button"
                aria-label={s.ariaLabel ?? s.label}
                aria-current={i === active ? "true" : undefined}
                onClick={() => scrollTo(i)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg pl-2 pr-2 py-1.5 transition-colors",
                  standaloneEnhancements && "min-h-11",
                  i === active
                    ? "bg-[rgb(var(--vs-accent-rgb)/0.12)] vs-accent-text"
                    : "text-muted-foreground/65 hover:bg-foreground/5 hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--vs-accent-rgb))]",
                )}
              >
                <span
                  className={cn(
                    "shrink-0 block rounded-full transition-all",
                    i === active
                      ? "h-2.5 w-2.5 bg-[rgb(var(--vs-accent-rgb))]"
                      : "h-1.5 w-1.5 bg-current",
                  )}
                />
                <span
                  className={cn(
                    "truncate text-left text-xs font-medium",
                    "opacity-0 group-hover/rail:opacity-100 group-focus-within/rail:opacity-100",
                    "transition-opacity duration-150",
                  )}
                >
                  {s.label}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </aside>
    </div>
  );
}
